import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";

const values = [
  {
    icon: "ri-flashlight-line",
    title: "Backup Power",
    desc: "Keep your home running when the grid goes down — whether it's a storm, an outage, or a prolonged emergency.",
  },
  {
    icon: "ri-sun-line",
    title: "Off-Grid Living",
    desc: "Energy independence isn't a trend — it's a real option. We carry the equipment that makes it practical and affordable.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Emergency Preparedness",
    desc: "Power is the foundation of any prepared household. We help families build that foundation with reliable, tested equipment.",
  },
  {
    icon: "ri-home-heart-line",
    title: "Everyday Self-Reliance",
    desc: "You don't need a crisis to benefit from being prepared. The best time to have a plan is before you need one.",
  },
];

const brands = [
  { name: "Renogy", note: "Authorized Reseller", status: "active" },
  { name: "Nature's Generator", note: "Authorized Reseller", status: "active" },
  { name: "Rich Solar", note: "Authorized Reseller", status: "active" },
  { name: "EcoFlow", note: "Coming Soon", status: "coming" },
];

export default function AboutPage() {
  return (
    <main className="font-sans bg-white min-h-screen">
      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "@id": "https://ironhavensupply.com/about#breadcrumb",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://ironhavensupply.com/" },
                  { "@type": "ListItem", position: 2, name: "About Us", item: "https://ironhavensupply.com/about" },
                ],
              },
              {
                "@type": "AboutPage",
                "@id": "https://ironhavensupply.com/about#webpage",
                name: "About Iron Haven Supply",
                url: "https://ironhavensupply.com/about",
                description: "Iron Haven Supply is a family-owned, authorized reseller of backup power systems — solar generators, off-grid kits, and emergency preparedness equipment for families who want to stay in control.",
                breadcrumb: { "@id": "https://ironhavensupply.com/about#breadcrumb" },
                about: { "@id": "https://ironhavensupply.com/#business" },
              },
              {
                "@type": "Organization",
            name: "Iron Haven Supply",
            legalName: "Furman Loomis LLC",
            taxID: "41-3937806",
            url: "https://ironhavensupply.com",
            foundingDate: "2020",
            address: {
              "@type": "PostalAddress",
              streetAddress: "2645 Broad St",
              addressLocality: "Bethel Park",
              addressRegion: "PA",
              postalCode: "15102",
              addressCountry: "US",
            },
            telephone: "+18666054975",
            email: "support@ironhavensupply.com",
            description:
              "Iron Haven Supply is a family-owned, authorized reseller of backup power systems — solar generators, off-grid kits, and emergency preparedness equipment for families who want to stay in control.",
              },
            ],
          }),
        }}
      />

      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100 pt-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "About Us" }]} />
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative min-h-[520px] md:min-h-[600px] flex items-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/b61e9d2a9d263cdcd453d8be55dc0d1f.jpg"
          alt="Iron Haven Supply — backup power for families who want to stay prepared"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/88 via-gray-900/65 to-gray-900/20" />
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-24 w-full">
          <div className="max-w-2xl">
            <span className="inline-block text-amber-400 text-xs font-bold uppercase tracking-widest mb-4">
              About Iron Haven Supply
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5">
              Built on Real Life —<br className="hidden md:block" /> Not Theory.
            </h1>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-8">
              We&apos;re a family-owned business started by real people who asked a simple question: <em>&ldquo;Why not be better prepared?&rdquo;</em> Everything we do comes back to that.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/shop"
                className="bg-amber-500 hover:bg-amber-400 text-white font-bold px-6 py-3 rounded-xl text-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                Shop Our Products
              </Link>
              <Link
                to="/contact"
                className="border border-white/40 hover:border-amber-400 text-white hover:text-amber-400 font-bold px-6 py-3 rounded-xl text-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                Talk to Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust bar ────────────────────────────────────────────────── */}
      <section className="bg-gray-900 py-7">
        <div className="max-w-6xl mx-auto px-4 md:px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: "Family-Owned", label: "Since Day One" },
            { value: "4", label: "Authorized Brands" },
            { value: "Zero Hype", label: "Just Real Products" },
            { value: "Always", label: "Here When You Need Us" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-amber-400 text-xl md:text-2xl font-extrabold">{s.value}</p>
              <p className="text-gray-400 text-xs mt-1 uppercase tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Our Story ────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2 mb-6 leading-tight">
                Iron Haven Supply Was Built on Real Life
              </h2>
              <div className="space-y-5 text-gray-600 text-sm md:text-base leading-relaxed">
                <p>
                  I&apos;m a <strong className="text-gray-800">construction recruiter by trade</strong> — working with builders, developers, and professionals who create the spaces we all live and work in. My background has always been rooted in understanding how things are built, what lasts, and what actually works in the real world.
                </p>
                <p>
                  At home, life looks a little different. My wife is a <strong className="text-gray-800">preschool teacher</strong>, shaping young minds every day. Together, we&apos;re <strong className="text-gray-800">foster parents and adoptive parents</strong> — raising our kids while navigating the same challenges most families face: rising costs, uncertainty, and the constant need to be prepared for whatever comes next.
                </p>
                <p>
                  That&apos;s where Iron Haven Supply comes in. We&apos;ve seen firsthand how quickly everyday life can be disrupted — power outages, supply issues, unexpected emergencies. And like most people, we started asking a simple question:
                </p>
              </div>

              {/* Pull quote */}
              <div className="mt-7 relative">
                <div className="absolute -left-1 top-0 bottom-0 w-1 bg-amber-400 rounded-full" />
                <blockquote className="pl-6">
                  <p className="text-xl md:text-2xl font-extrabold text-gray-900 leading-snug">
                    &ldquo;Why not be better prepared?&rdquo;
                  </p>
                  <footer className="text-xs text-gray-400 font-medium mt-2 uppercase tracking-widest">
                    — The question that started everything
                  </footer>
                </blockquote>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20family%20home%20living%20room%20with%20portable%20power%20station%20solar%20generator%20on%20side%20table%20near%20window%20cozy%20interior%20photography%20natural%20sunlight%20coming%20through%20window%20plants%20books%20shelves%20family%20home%20environment%20inviting%20atmosphere%20editorial%20lifestyle%20photography&width=700&height=560&seq=about-story-home-ihs&orientation=landscape"
                alt="Iron Haven Supply — family backup power preparedness"
                className="w-full h-72 md:h-[420px] object-cover object-center rounded-2xl"
              />
              <div className="absolute -bottom-5 -right-4 bg-amber-500 text-white rounded-xl px-5 py-4 hidden md:block">
                <p className="font-extrabold text-base leading-tight">Family-Owned</p>
                <p className="text-amber-100 text-xs mt-0.5">Real people. Real mission.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission callout ──────────────────────────────────────────── */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-amber-500/20 rounded-2xl mx-auto mb-6">
            <i className="ri-heart-3-line text-amber-400 text-3xl"></i>
          </div>
          <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-4">Our Mission</p>
          <h2 className="text-2xl md:text-4xl font-extrabold text-white leading-snug mb-6">
            Help families stay prepared,<br className="hidden md:block" /> protected, and in control.
          </h2>
          <p className="text-gray-400 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
            Because at the end of the day, this isn&apos;t just a business. It&apos;s how we take care of our own family — and how we help you take care of yours.
          </p>
        </div>
      </section>

      {/* ── What We Carry ────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">What We&apos;re About</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">
              Practical Solutions for Real Families
            </h2>
            <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
              We&apos;re not here to sell hype. We focus on products that solve real problems — equipment you can depend on when it matters most.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-xl p-6 border border-gray-100 flex gap-5 items-start">
                <div className="w-11 h-11 flex items-center justify-center bg-amber-50 rounded-xl shrink-0">
                  <i className={`${v.icon} text-amber-500 text-xl`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base mb-1">{v.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Family behind the brand ──────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">The People Behind It</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">
              This Is Personal
            </h2>
          </div>

          <div className="space-y-5 text-gray-600 text-sm md:text-base leading-relaxed">
            <p>
              We didn&apos;t start this business with investors or a business plan on a whiteboard. We started it because <strong className="text-gray-800">we needed what we sell</strong>.
            </p>
            <p>
              As a foster and adoptive family, we know firsthand that life can change in an instant. Keeping our kids safe, our home running, and our family stable isn&apos;t abstract — it&apos;s daily life.
            </p>
            <p>
              My background in construction gave me a practical eye for what actually works vs. what just looks good on a spec sheet. And being surrounded by teachers, builders, and real families every day keeps the mission grounded.
            </p>
            <p>
              <strong className="text-gray-800">We don&apos;t carry products we wouldn&apos;t use ourselves.</strong> Every brand we stock is one we believe in — not because of a marketing deal, but because the product genuinely delivers.
            </p>

            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 flex-1">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                  <i className="ri-user-heart-line text-amber-600"></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800">The Founder</p>
                  <p className="text-xs text-gray-500 mt-0.5">Construction recruiter, husband, foster &amp; adoptive dad</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 flex-1">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                  <i className="ri-seedling-line text-amber-600"></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800">His Wife</p>
                  <p className="text-xs text-gray-500 mt-0.5">Preschool teacher, co-pilot, heart of the operation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Authorized brands ────────────────────────────────────────── */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">Brand Partnerships</span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-2">
              Authorized Reseller — Every Purchase Protected
            </h2>
            <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
              Every brand we carry has verified our business identity and authorized us to sell their products. Your manufacturer warranty is valid on every purchase from Iron Haven.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-3xl mx-auto">
            {brands.map((b) => (
              <div
                key={b.name}
                className={`rounded-xl p-5 flex flex-col items-center gap-3 border ${
                  b.status === "coming"
                    ? "bg-gray-50 border-dashed border-gray-200 opacity-70"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${b.status === "coming" ? "bg-gray-100" : "bg-amber-50"}`}>
                  <i className={`text-2xl ${b.status === "coming" ? "ri-time-line text-gray-400" : "ri-verified-badge-line text-amber-500"}`}></i>
                </div>
                <p className="font-extrabold text-gray-900 text-sm text-center">{b.name}</p>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                  b.status === "coming"
                    ? "bg-gray-100 text-gray-500"
                    : "bg-amber-50 text-amber-700"
                }`}>
                  {b.note}
                </span>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-400 mt-6">
            Authorized reseller status is maintained through active brand agreements.{" "}
            <Link to="/warranty" className="text-amber-500 hover:underline cursor-pointer">
              View our warranty coverage page
            </Link>{" "}
            for per-brand terms.
          </p>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
            Questions? We&apos;d love to hear from you.
          </h2>
          <p className="text-gray-400 text-sm md:text-base mb-8 max-w-xl mx-auto">
            Whether you&apos;re comparing systems, figuring out what your home actually needs, or just want to talk through options — our team picks up the phone.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/shop"
              className="bg-amber-500 hover:bg-amber-400 text-white font-bold px-7 py-3 rounded-xl text-sm transition-colors whitespace-nowrap cursor-pointer"
            >
              Shop Our Products
            </Link>
            <Link
              to="/contact"
              className="border border-gray-600 hover:border-amber-400 text-gray-300 hover:text-amber-400 font-bold px-7 py-3 rounded-xl text-sm transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Our Team
            </Link>
            <Link
              to="/faq"
              className="border border-gray-600 hover:border-amber-400 text-gray-300 hover:text-amber-400 font-bold px-7 py-3 rounded-xl text-sm transition-colors whitespace-nowrap cursor-pointer"
            >
              Browse the FAQ
            </Link>
          </div>
          <p className="text-gray-500 text-xs mt-8">
            Iron Haven Supply ·{" "}
            <a href="tel:8666054975" className="text-amber-400 hover:underline cursor-pointer">
              (866) 605-4975
            </a>{" "}
            · Mon–Fri, 9am–5pm EST
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
